import { getAtPath, setAtPath } from './configPaths'

// Conditions are shared by fixed fields, collection items, and option availability.

function conditionValue(condition, configuration, item) {
  if (condition.itemPath) return getAtPath(item, condition.itemPath)
  return getAtPath(configuration, condition.path)
}

export function matchesCondition(condition, configuration, item = null) {
  if (!condition) return true
  if (Array.isArray(condition.all)) {
    return condition.all.every((entry) => matchesCondition(entry, configuration, item))
  }
  if (Array.isArray(condition.any)) {
    return condition.any.some((entry) => matchesCondition(entry, configuration, item))
  }

  const value = conditionValue(condition, configuration, item)
  if ('equals' in condition) return value === condition.equals
  if ('notEquals' in condition) return value !== condition.notEquals
  if ('in' in condition) return condition.in.includes(value)
  if ('notIn' in condition) return !condition.notIn.includes(value)
  if ('exists' in condition) {
    const exists = value !== null && value !== undefined && value !== ''
    return condition.exists ? exists : !exists
  }
  return true
}

export function visibleOptions(options, configuration, item = null) {
  const format = getAtPath(configuration, 'format.id')
  const layout = getAtPath(configuration, 'design.layout')

  return (options || []).filter((option) => {
    if (option.formats && !option.formats.includes(format)) return false
    if (option.layouts && !option.layouts.includes(layout)) return false
    if (!matchesCondition(option.availableWhen, configuration, item)) return false
    return true
  })
}

/**
 * Options may be limited to formats/layouts (`option.formats`, `option.layouts`,
 * `option.availableWhen`). After a format or template change the stored value can point at
 * an option that no longer exists for the new format — the server then rejects the save
 * ("The selected layout is not available for this format."). Move every such value to the
 * first option that is still available. Two passes: layout-dependent options settle after
 * `design.layout` itself has been corrected.
 */
export function normalizeConditionalOptions(schema, configuration) {
  if (!schema || !configuration) return configuration
  let next = configuration

  const fix = (field, basePath, item) => {
    if (!field || !Array.isArray(field.options) || !field.options.length) return
    if (!['select', 'segmented'].includes(field.type)) return
    if (!field.options.some((option) => option.formats || option.layouts || option.availableWhen)) return
    const path = basePath ? `${basePath}.${field.path}` : field.path
    const value = getAtPath(next, path)
    const visible = visibleOptions(field.options, next, item)
    if (!visible.length || visible.some((option) => option.value === value)) return
    next = setAtPath(next, path, visible[0].value)
  }

  for (let pass = 0; pass < 2; pass += 1) {
    for (const section of schema.sections || []) {
      if (section.collection) continue
      for (const field of section.fields || []) fix(field, '', null)
    }
    for (const collection of schema.collections || []) {
      if (!collection?.path) continue
      const items = getAtPath(next, collection.path)
      if (!Array.isArray(items)) continue
      items.forEach((item, index) => {
        for (const itemSection of collection.itemSections || []) {
          for (const field of itemSection.fields || []) fix(field, `${collection.path}.${index}`, item)
        }
      })
    }
  }

  return next
}
